/**
 * Environment access, validated once at startup.
 *
 * A missing Supabase URL should fail loudly on boot with a readable message,
 * not as `undefined is not a valid URL` from deep inside the client library.
 */

interface AppEnv {
  supabaseUrl: string;
  supabaseAnonKey: string;
  authRedirectUrl: string;
}

function required(name: string, value: string | undefined): string {
  if (!value || value.trim() === '') {
    throw new Error(
      `Missing ${name}. Copy apps/web/.env.example to apps/web/.env.local and fill it in.`,
    );
  }
  return value.trim();
}

export function readEnv(source: ImportMetaEnv = import.meta.env): AppEnv {
  const supabaseUrl = required('VITE_SUPABASE_URL', source.VITE_SUPABASE_URL);

  try {
    new URL(supabaseUrl);
  } catch {
    throw new Error(`VITE_SUPABASE_URL is not a valid URL: ${supabaseUrl}`);
  }

  return {
    supabaseUrl,
    supabaseAnonKey: required('VITE_SUPABASE_ANON_KEY', source.VITE_SUPABASE_ANON_KEY),
    authRedirectUrl:
      source.VITE_AUTH_REDIRECT_URL?.trim() || `${window.location.origin}/auth/callback`,
  };
}

export const env: AppEnv = readEnv();
